import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { FileUIPart, UIMessage } from 'ai';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 } from 'uuid';

import { useApiClient } from '@eluve/api-client-provider';
import { getEndpointUrl } from '@eluve/api-contract';
import { SettingsPageFrame } from '@eluve/blocks';
import { MemoizedMarkdownProse, PlanShapeBadge } from '@eluve/care-plan-render';
import {
  Button,
  Heading,
  Icon,
  LinkButton,
  LoadingIcon,
  Text,
  Textarea,
  cn,
  useToast,
} from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';
import { useMedplum } from '@eluve/medplum-client';
import {
  PLAN_SHAPES,
  type PlanDraft,
  type PlanShapeCode,
  type PlanSpec,
  coercePartialPlanSpec,
} from '@eluve/plan-shapes';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { type DraftingState, PlanDraftPreview } from './PlanDraftPreview';
import {
  ACCEPTED_TYPES,
  AttachmentStrip,
  attachmentFileParts,
  outgoingFiles,
  useAttachments,
} from './attachments';
import { planSaveBundle } from './care-plans';
import { messageRenderKey, messageViewPropsEqual } from './chatMessageMemo';
import {
  type ToolUIPart,
  isPlanDraft,
  priorInvalidIssueCount,
  toolDisplay,
  toolLabel,
} from './drafting-activity';

// Static empty-state chips: one worked example per Plan Shape. Prompts are
// i18n message keys resolved with `t()` at render time (the picked prompt is
// sent to the agent in the user's UI language).
const SHAPE_SUGGESTIONS: Array<{
  shape: PlanShapeCode;
  promptKey: MessageKey;
}> = [
  { shape: 'phased', promptKey: 'carePlans.builder.suggestionPhased' },
  { shape: 'episodic', promptKey: 'carePlans.builder.suggestionEpisodic' },
  {
    shape: 'continuous',
    promptKey: 'carePlans.builder.suggestionContinuous',
  },
];

const PlanBuilderContent: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const toast = useToast();
  const { client: medplumClient } = useMedplum();
  const [searchParams, setSearchParams] = useSearchParams();
  const thread = searchParams.get('thread') ?? undefined;

  // `?thread=` deep-links the conversation — the living draft is conversation
  // state (the latest presentPlan emission), so restoring the thread restores
  // the draft.
  const ephemeralIdRef = useRef<string>(v4());
  const activeThreadId = thread ?? ephemeralIdRef.current;
  const threadIdRef = useRef<string>(activeThreadId);
  threadIdRef.current = activeThreadId;
  const searchThreadRef = useRef<string | undefined>(thread);
  searchThreadRef.current = thread;
  // Which thread's transcript is materialized in useChat (null = fresh slate).
  const loadedThreadRef = useRef<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getEndpointUrl(baseUrl, 'planBuilder', { params: { tenantId } }),
        credentials: 'include',
        prepareSendMessagesRequest: ({ messages: chatMessages }) => {
          const last = chatMessages.at(-1);
          const text =
            last?.parts
              .filter(
                (p): p is { type: 'text'; text: string } => p.type === 'text',
              )
              .map((p) => p.text)
              .join('') ?? '';
          const files = last ? outgoingFiles(last) : [];
          return {
            body: {
              text,
              ...(files.length > 0 && { files }),
              threadId: threadIdRef.current,
            },
          };
        },
      }),
    [baseUrl, tenantId],
  );

  const { messages, sendMessage, status, setMessages, stop } = useChat({
    transport,
    experimental_throttle: 50,
    onFinish: () => {
      // First turn persisted the thread — promote it to the URL so the draft
      // conversation is deep-linkable and survives refresh.
      const id = threadIdRef.current;
      if (searchThreadRef.current !== id) {
        loadedThreadRef.current = id;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('thread', id);
            return next;
          },
          { replace: true },
        );
      }
    },
  });

  const busy = status === 'submitted' || status === 'streaming';

  // Hydrate useChat from a stored conversation (deep link / back-forward).
  useEffect(() => {
    if (!thread || loadedThreadRef.current === thread) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.mastra.getPlanBuilderThread({
          params: { tenantId, threadId: thread },
        });
        if (cancelled) {
          return;
        }
        stop();
        setMessages(
          result.status === 200 ? (result.body.messages as UIMessage[]) : [],
        );
        loadedThreadRef.current = thread;
      } catch {
        if (!cancelled) {
          stop();
          setMessages([]);
          loadedThreadRef.current = thread;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [thread, tenantId, apiClient, stop, setMessages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  // The living draft: the LATEST presentPlan emission across the transcript
  // is the canonical draft (full-replace semantics).
  const draft = useMemo<PlanDraft | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.role !== 'assistant') {
        continue;
      }
      for (let j = message.parts.length - 1; j >= 0; j--) {
        const part = message.parts[j] as ToolUIPart;
        if (
          part.type === 'tool-presentPlan' &&
          part.state === 'output-available' &&
          isPlanDraft(part.output)
        ) {
          return part.output;
        }
      }
    }
    return null;
  }, [messages]);

  // The in-flight emission: presentPlan streams the COMPLETE spec as its
  // tool input, so while it streams the coerced prefix renders the draft
  // building live in the preview pane. Gated on `busy` so a stopped stream
  // falls back to the last settled draft.
  const drafting = useMemo<DraftingState | null>(() => {
    if (!busy) {
      return null;
    }
    const last = messages.at(-1);
    if (last?.role !== 'assistant') {
      return null;
    }
    const parts = last.parts as ToolUIPart[];
    for (let j = parts.length - 1; j >= 0; j--) {
      const part = parts[j];
      if (part?.type !== 'tool-presentPlan') {
        continue;
      }
      if (
        part.state !== 'input-streaming' &&
        part.state !== 'input-available'
      ) {
        return null;
      }
      // A re-emission after a prior emission settled invalid is a fix cycle.
      const issueCount = priorInvalidIssueCount(parts, j);
      return {
        plan: coercePartialPlanSpec(
          (part.input as { plan?: unknown } | undefined)?.plan,
        ),
        stage: part.state === 'input-streaming' ? 'drafting' : 'validating',
        resolvingIssues: issueCount > 0,
        issueCount,
      };
    }
    return null;
  }, [messages, busy]);

  // Save-is-publish: compile the spec (the same deterministic compile the
  // $validate gate exercised) into a transaction Bundle — the PlanDefinition
  // plus its ActivityDefinitions, all-or-nothing — and post it under the
  // admin's own Medplum token. The agent never writes.
  const [saving, setSaving] = useState(false);
  const handleSave = async (toSave: PlanDraft) => {
    if (!medplumClient) {
      toast.error(t('carePlans.builder.medplumUnavailable'));
      return;
    }
    setSaving(true);
    try {
      const result = await medplumClient.executeBatch(
        planSaveBundle(toSave.plan),
      );
      const created = result.entry
        ?.map((e) => e.resource)
        .find((r) => r?.resourceType === 'PlanDefinition');
      if (!created?.id) {
        throw new Error(
          'the transaction did not return the created PlanDefinition',
        );
      }
      // The list page re-runs a PlanDefinition search on mount, which Medplum
      // serves from its in-memory request cache. Without invalidating it, the
      // freshly-saved plan won't appear there until a hard refresh rebuilds the
      // client cache. The save bundle also writes ActivityDefinitions, so
      // invalidate those searches too.
      medplumClient.invalidateSearches('PlanDefinition');
      medplumClient.invalidateSearches('ActivityDefinition');
      toast.success(t('carePlans.builder.planSaved'));
      navigate(`../${created.id}`);
    } catch (err) {
      toast.error(
        t('carePlans.builder.saveFailed', {
          error: err instanceof Error ? err.message : String(err),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-12rem)] min-h-96 flex-col p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
        <div>
          <Heading size="lg" weight="semibold" level={2}>
            {t('carePlans.builder.title')}
          </Heading>
          <Text color="secondary">{t('carePlans.builder.subtitle')}</Text>
        </div>
        <LinkButton to=".." variant="outline">
          <Icon name="ArrowLeft" size="auto" />
          {t('carePlans.templates.allCarePlans')}
        </LinkButton>
      </div>

      <div className="flex min-h-0 flex-1 rounded-xl border border-border bg-background">
        {/* Chat pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4">
            {messages.length === 0 ? (
              <EmptyState onPick={(q) => sendMessage({ text: q })} />
            ) : (
              <div className="mx-auto flex max-w-2xl flex-col gap-6">
                {messages.map((m) => (
                  <MessageView
                    key={m.id}
                    message={m}
                    busy={busy}
                    renderKey={messageRenderKey(m)}
                  />
                ))}
              </div>
            )}
          </div>
          <Composer
            onSubmit={(text, files) =>
              text.length > 0
                ? sendMessage({
                    text,
                    files: files.length > 0 ? files : undefined,
                  })
                : sendMessage({ files })
            }
            onStop={stop}
            busy={busy}
          />
        </div>

        {/* Persistent preview pane: the living draft. */}
        <aside className="w-120 shrink-0 border-l border-border bg-bg-secondary xl:w-[55%]">
          <PlanDraftPreview
            draft={draft}
            drafting={drafting}
            onSave={handleSave}
            saving={saving}
          />
        </aside>
      </div>
    </div>
  );
};

export const PlanBuilderPage: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { t } = useTranslation();
  return (
    <SettingsPageFrame
      size="wide"
      parentTitle={t('carePlans.templates.pageTitle')}
      padded={false}
    >
      <PlanBuilderContent baseUrl={baseUrl} />
    </SettingsPageFrame>
  );
};

const MessageView = memo(function MessageView({
  message,
  busy,
}: {
  message: UIMessage;
  busy: boolean;
  // Immutable per-render signature, compared by `messageViewPropsEqual`. Not
  // read in the body — it exists solely so the memo can detect changes to the
  // in-place-mutated streaming message.
  renderKey: string;
}) {
  const { t } = useTranslation();
  if (message.role === 'user') {
    const text = message.parts
      .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map((p) => p.text)
      .join('');
    const files = attachmentFileParts(message);
    return (
      <div className="flex max-w-[85%] flex-col items-end gap-2 self-end">
        {files.length > 0 ? (
          <div className="flex flex-wrap justify-end gap-2">
            {files.map((file, i) =>
              file.mediaType.startsWith('image/') ? (
                <img
                  key={i}
                  src={file.url}
                  alt={file.filename ?? t('carePlans.builder.attachedImageAlt')}
                  className="max-h-32 rounded-lg border border-border object-cover"
                />
              ) : (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2"
                  title={file.filename}
                >
                  <Icon
                    name="FileText"
                    size="auto"
                    className="size-5 shrink-0 text-muted-foreground"
                  />
                  <Text size="xs" className="max-w-40 truncate">
                    {file.filename ?? 'document.pdf'}
                  </Text>
                </div>
              ),
            )}
          </div>
        ) : null}
        {text.length > 0 ? (
          <div className="rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-sm text-primary-foreground">
            {text}
          </div>
        ) : null}
      </div>
    );
  }

  const hasVisible = message.parts.some(
    (p) =>
      (p.type === 'text' && p.text.length > 0) || p.type.startsWith('tool-'),
  );

  return (
    <div className="flex flex-col gap-2">
      {!hasVisible && busy ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Sparkles" size="auto" className="size-4 animate-pulse" />
          {t('carePlans.chat.thinking')}
        </div>
      ) : null}

      {message.parts.map((part, i) => {
        if (part.type === 'text') {
          return part.text.length > 0 ? (
            <MemoizedMarkdownProse
              key={i}
              content={part.text}
              className="text-sm text-foreground"
            />
          ) : null;
        }
        if (part.type.startsWith('tool-')) {
          return (
            <ToolPart
              key={i}
              part={part as unknown as ToolUIPart}
              parts={message.parts as unknown as ToolUIPart[]}
              index={i}
            />
          );
        }
        return null;
      })}
    </div>
  );
}, messageViewPropsEqual);

function ToolPart({
  part,
  parts,
  index,
}: {
  part: ToolUIPart;
  parts: ToolUIPart[];
  index: number;
}) {
  const { t } = useTranslation();
  const toolName = part.type.slice('tool-'.length);

  // The draft itself renders in the persistent preview pane — the transcript
  // just marks the emission and its $validate verdict. While the emission
  // streams, the chip ticks up with real progress from the partial spec.
  if (toolName === 'presentPlan') {
    if (part.state === 'input-streaming' || part.state === 'input-available') {
      return (
        <DraftingStep
          stage={part.state}
          spec={coercePartialPlanSpec(
            (part.input as { plan?: unknown } | undefined)?.plan,
          )}
          resolvingIssues={priorInvalidIssueCount(parts, index)}
        />
      );
    }
    if (part.state !== 'output-available' || !isPlanDraft(part.output)) {
      return (
        <FhirToolStep
          toolName={toolName}
          state={part.state}
          errorText={part.errorText}
        />
      );
    }
    const draft = part.output;
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-1.5 text-xs">
        <Icon
          name="Sparkles"
          size="auto"
          className="size-3.5 shrink-0 text-muted-foreground"
        />
        <span className="font-medium text-foreground">
          {t('carePlans.chat.draftUpdated')}
        </span>
        <PlanShapeBadge shape={draft.plan.shape} />
        {draft.valid ? (
          <span className="ml-auto inline-flex items-center gap-1 text-text-positive-subtle">
            {t('carePlans.chat.valid')}
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-text-warning-subtle">
            {t(
              draft.issues.length === 1
                ? 'carePlans.chat.issueCountOne'
                : 'carePlans.chat.issueCountMany',
              { count: draft.issues.length },
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <FhirToolStep
      toolName={toolName}
      state={part.state}
      input={part.input}
      errorText={part.errorText}
    />
  );
}

// A compact transcript chip for the exploration tools (fhirSearch / fhirRead),
// rendered through the shared tool-status pattern: a human-readable active label
// with a live activity cue while the call runs, a settled label with a check
// once it returns — so a paused FHIR search reads as work-in-progress, not a
// stall.
function FhirToolStep({
  toolName,
  state,
  input,
  errorText,
}: {
  toolName: string;
  state: ToolUIPart['state'];
  input?: unknown;
  errorText?: string;
}) {
  const { t } = useTranslation();
  const detail =
    input && typeof input === 'object' && 'resourceType' in input
      ? String((input as { resourceType?: unknown }).resourceType ?? '')
      : '';
  const complete = state === 'output-available';
  const errored = state === 'output-error';
  const running = state === 'input-streaming' || state === 'input-available';
  const display = toolDisplay(toolName);
  return (
    <div
      className={cn(
        'inline-flex w-fit max-w-full items-center gap-2 rounded-full border px-3 py-1 text-[11px] transition-colors',
        complete &&
          'border-border-positive-subtle bg-bg-positive-subtle text-text-positive-subtle',
        errored &&
          'border-border-warning-subtle bg-bg-warning-subtle text-text-warning-subtle',
        !complete &&
          !errored &&
          'border-border-insights-subtle bg-bg-insights-subtle text-text-insights',
      )}
    >
      <Icon
        name={
          complete ? 'CheckCircle2' : errored ? 'AlertTriangle' : display.icon
        }
        size="auto"
        className="size-3.5 shrink-0"
      />
      <span className="font-medium">
        {t(errored ? display.errorLabel : toolLabel(toolName, running))}
      </span>
      {errored && errorText ? (
        <span className="opacity-80">{errorText}</span>
      ) : detail ? (
        <span className="font-mono opacity-70">{detail}</span>
      ) : null}
      {running ? (
        <LoadingIcon size="auto" className="size-3.5 shrink-0" />
      ) : null}
    </div>
  );
}

// Live progress for the streaming emission, from the coerced partial spec —
// real counts, not spinner theater.
function draftingProgress(
  spec: PlanSpec | null,
  t: ReturnType<typeof useTranslation>['t'],
): string | null {
  if (!spec) {
    return null;
  }
  const n = (count: number, one: MessageKey, many: MessageKey) =>
    t(count === 1 ? one : many, { count });
  const activityCount = (count: number) =>
    n(count, 'carePlans.counts.activityOne', 'carePlans.counts.activityMany');
  if (spec.shape === 'phased') {
    const activities = spec.phases.reduce(
      (sum, p) => sum + (p.activities?.length ?? 0),
      0,
    );
    return [
      n(
        spec.phases.length,
        'carePlans.counts.phaseOne',
        'carePlans.counts.phaseMany',
      ),
      activities > 0 ? activityCount(activities) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (spec.shape === 'episodic') {
    const activities =
      (spec.baselineActivities?.length ?? 0) +
      spec.events.reduce((sum, e) => sum + (e.activities?.length ?? 0), 0);
    return [
      n(
        spec.events.length,
        'carePlans.counts.eventOne',
        'carePlans.counts.eventMany',
      ),
      activities > 0 ? activityCount(activities) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  const activities = spec.activities?.length ?? 0;
  return [
    n(
      spec.bands.length,
      'carePlans.counts.bandOne',
      'carePlans.counts.bandMany',
    ),
    activities > 0 ? activityCount(activities) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function DraftingStep({
  stage,
  spec,
  resolvingIssues,
}: {
  stage: 'input-streaming' | 'input-available';
  spec: PlanSpec | null;
  resolvingIssues: number;
}) {
  const { t } = useTranslation();
  const progress = draftingProgress(spec, t);
  // While the fix emission streams it's "Resolving …"; the $validate round-trip
  // that follows (input-available) is "Validating …" either way.
  const label =
    stage === 'input-available'
      ? t('carePlans.chat.validatingWithMedplum')
      : resolvingIssues > 0
        ? t(
            resolvingIssues === 1
              ? 'carePlans.builder.resolvingIssuesOne'
              : 'carePlans.builder.resolvingIssuesMany',
            { count: resolvingIssues },
          )
        : t('carePlans.builder.draftingPlan');
  return (
    <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-full bg-bg-strong-primary px-3 py-1.5 text-xs text-text-strong">
      <Icon name="Sparkles" size="auto" className="size-3.5 shrink-0" />
      <span className="font-medium">{label}</span>
      {progress ? (
        <Text inline size="xs" color="inherit" className="font-mono opacity-70">
          {progress}
        </Text>
      ) : null}
      <LoadingIcon size="auto" className="size-3.5 shrink-0" />
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (q: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-16 text-center">
      <div className="grid size-12 place-items-center rounded-2xl bg-bg-strong-primary text-text-strong">
        <Icon name="Sparkles" size="auto" className="size-6" />
      </div>
      <div className="space-y-1">
        <Heading size="lg" weight="semibold" level={2}>
          {t('carePlans.builder.emptyTitle')}
        </Heading>
        <Text color="secondary">{t('carePlans.builder.emptyBody')}</Text>
      </div>
      <div className="flex w-full flex-col gap-2 text-left">
        {SHAPE_SUGGESTIONS.map(({ shape, promptKey }) => (
          <button
            key={shape}
            type="button"
            onClick={() => onPick(t(promptKey))}
            className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-muted"
          >
            <span className="mb-1 flex items-center gap-2">
              <PlanShapeBadge shape={shape} />
              <Text inline size="xs" color="secondary">
                {PLAN_SHAPES[shape].blurb}
              </Text>
            </span>
            {t(promptKey)}
          </button>
        ))}
      </div>
    </div>
  );
}

function Composer({
  onSubmit,
  onStop,
  busy,
}: {
  onSubmit: (text: string, files: FileUIPart[]) => void;
  onStop: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { attachments, addFiles, removeAt, clear } = useAttachments();
  const [dragActive, setDragActive] = useState(false);

  const submit = () => {
    const text = ref.current?.value.trim() ?? '';
    // A self-describing attachment is a valid message on its own — require text
    // only when nothing is attached.
    if ((text.length === 0 && attachments.length === 0) || busy) {
      return;
    }
    onSubmit(text, attachments);
    clear();
    if (ref.current) {
      ref.current.value = '';
    }
  };
  return (
    <div className="border-t border-border bg-background px-6 py-3">
      {/* Attachments arrive three ways: the picker button, paste into the
          textarea, or drag-and-drop anywhere on the composer. */}
      <div
        className={cn(
          'mx-auto max-w-2xl rounded-xl',
          dragActive && 'ring-2 ring-ring/50',
        )}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDragActive(true);
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragActive(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          void addFiles(e.dataTransfer.files);
        }}
      >
        <AttachmentStrip attachments={attachments} onRemove={removeAt} />
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            hidden
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            aria-label={t('carePlans.builder.attachFiles')}
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="Paperclip" size="auto" />
          </Button>
          <Textarea
            ref={ref}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            onPaste={(e) => {
              if (e.clipboardData.files.length > 0) {
                e.preventDefault();
                void addFiles(e.clipboardData.files);
              }
            }}
            placeholder={t('carePlans.builder.composerPlaceholder')}
            rows={1}
            className="max-h-40 min-h-11 flex-1 resize-none shadow-sm"
          />
          {busy ? (
            <Button onClick={onStop} variant="outline">
              <Icon name="Square" size="auto" className="fill-current" />
              {t('carePlans.chat.stop')}
            </Button>
          ) : (
            <Button onClick={submit}>
              <Icon name="CornerDownLeft" size="auto" />
              {t('carePlans.chat.send')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
